import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useAgentRunList } from "../api/queries/agents";
import {
  LayoutGrid,
  Database,
  ChevronsRight,
  MessageSquare,
  Users,
  LayoutDashboard,
  Activity,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Scale,
  ShieldAlert,
} from "lucide-react";

function Sidebar() {
  const [coworkOpen, setCoworkOpen] = useState(true);
  const [chatOpen, setChatOpen] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Sidebar badge counts — share the same query cache as the Agents page,
  // so opening it hits the cache instantly.
  const agentRuns = useAgentRunList(500);
  const agentRunCount = agentRuns.data?.length ?? null;

  // Initial for the avatar — uppercase first letter of name or email.
  const initial = (user.name?.[0] ?? user.email[0] ?? "?").toUpperCase();
  const displayName = user.name ?? user.email.split("@")[0];

  return (
    <aside className="sidebar w-60 flex flex-col sticky top-0 h-screen p-3">
      {/* ── Logo block ──────────────────────────────────────────────
          Matte ink square with a thin gold inner stroke — replaces the
          purple→pink gradient that read as fintech. Subtitle changed
          to convey gravitas, not friendliness. */}
      <div className="flex items-center gap-2.5 px-2 py-2 mb-2">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center"
          style={{
            background: "var(--color-accent-strong)",
            boxShadow: "inset 0 0 0 1px var(--color-highlight)",
          }}
        >
          <LayoutGrid size={15} color="white" strokeWidth={2} />
        </div>
        <div className="leading-tight">
          <div
            className="font-semibold text-fg-strong text-[15px]"
            style={{ fontFamily: "var(--font-serif)", letterSpacing: "-0.012em" }}
          >
            OpenWatch
          </div>
          <div className="text-[11px] text-fg-muted">AI governance for your firm</div>
        </div>
      </div>

      {/* ── Primary nav ─────────────────────────────────────────── */}
      <nav className="flex flex-col gap-0.5 px-1 mt-2">
        <NavLink to="/dashboard" className={navLinkClass}>
          <LayoutDashboard />
          Overview
        </NavLink>

        <NavLink to="/datasources" className={navLinkClass}>
          <Database />
          Data Sources
        </NavLink>

        {/* Cowork group */}
        <NavGroup
          label="Cowork"
          icon={<ChevronsRight />}
          open={coworkOpen}
          onToggle={() => setCoworkOpen((v) => !v)}
        >
          <SubLink to="/sessions">Sessions</SubLink>
          <SubLink to="/projects">Projects</SubLink>
          <SubLink to="/tools">Tool Usage</SubLink>
        </NavGroup>

        {/* Chat group */}
        <NavGroup
          label="Chat"
          icon={<MessageSquare />}
          open={chatOpen}
          onToggle={() => setChatOpen((v) => !v)}
        >
          <SubLink to="/compliance">Messages</SubLink>
        </NavGroup>

        {user.role === "admin" && (
          <NavLink to="/users" className={navLinkClass}>
            <Users />
            Users
          </NavLink>
        )}
      </nav>

      {/* ── Surfaces section — pages dedicated to a specific Claude
          surface the firm uses (Office Agent, Mike). Sits between the
          generic data views and the agents stack so partners find it
          near "what attorneys are doing". */}
      <div className="nav-section">Surfaces</div>
      <nav className="flex flex-col gap-0.5 px-1">
        <NavLink to="/office" className={navLinkClass}>
          <Briefcase />
          Claude in Microsoft 365
        </NavLink>
        <NavLink to="/mike" className={navLinkClass}>
          <Scale />
          Mike
        </NavLink>
      </nav>

      {/* ── Agents section ──────────────────────────────────────── */}
      <div className="nav-section">Agents</div>
      <nav className="flex flex-col gap-0.5 px-1">
        <NavLink to="/catalog" className={navLinkClass}>
          <LayoutGrid />
          Agent Catalog
        </NavLink>
        <NavLink to="/agents" className={navLinkClass}>
          <Activity />
          Agent Activity
          {agentRunCount !== null && agentRunCount > 0 && (
            <span
              className="ml-auto text-[10.5px] mono px-1.5 py-0.5 rounded"
              style={{
                background: "var(--color-success-soft)",
                color: "#047857",
                border: "1px solid var(--color-success-border)",
              }}
            >
              {agentRunCount}
            </span>
          )}
        </NavLink>
      </nav>

      {/* ── Admin section ── only firm administrators see this. */}
      {user.role === "admin" && (
        <>
          <div className="nav-section">Admin</div>
          <nav className="flex flex-col gap-0.5 px-1">
            <NavLink to="/admin/policy" className={navLinkClass}>
              <ShieldAlert />
              Firm policy
            </NavLink>
          </nav>
        </>
      )}

      {/* ── User footer ──────────────────────────────────────────
          Opens the Account & Workspace page — organisation profile,
          compliance posture, active integrations, and account team. */}
      <div className="mt-auto pt-3 border-t border-border-subtle mx-1">
        <button
          onClick={() => navigate("/account")}
          className="flex items-center gap-2.5 px-2 py-1.5 rounded-md w-full hover:bg-bg-hover transition-colors text-left"
          aria-label="Open account and workspace settings"
        >
          <div className="avatar" style={{ background: "var(--color-success)" }}>
            {initial}
          </div>
          <div className="flex-1 min-w-0 leading-tight">
            <div className="text-[12.5px] text-fg-strong font-medium truncate">{displayName}</div>
            <div className="text-[11px] text-fg-muted truncate">{user.email}</div>
          </div>
          <ChevronRight size={12} className="text-fg-muted" />
        </button>
      </div>
    </aside>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function navLinkClass({ isActive }: { isActive: boolean }) {
  return `nav-link ${isActive ? "active" : ""}`;
}

function NavGroup({
  label,
  icon,
  open,
  onToggle,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="flex items-center gap-1 px-1 mt-1.5 rounded-md hover:bg-bg-hover transition-colors">
        <span className="flex items-center gap-2 flex-1 px-1.5 py-1.5 text-[13px] font-medium text-fg-default cursor-pointer select-none">
          <span className="text-fg-muted [&>svg]:w-4 [&>svg]:h-4">{icon}</span>
          {label}
        </span>
        <button
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`Toggle ${label}`}
          className="flex items-center justify-center w-[22px] h-[22px] rounded text-fg-muted hover:bg-bg-surface-2 hover:text-fg-default transition-colors"
        >
          <ChevronDown
            size={12}
            className="transition-transform"
            style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}
          />
        </button>
      </div>
      <div
        className="flex flex-col gap-px overflow-hidden transition-all"
        style={{
          maxHeight: open ? "400px" : "0",
          opacity: open ? 1 : 0,
          paddingTop: open ? "2px" : 0,
          paddingBottom: open ? "4px" : 0,
        }}
      >
        {children}
      </div>
    </>
  );
}

function SubLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `nav-link relative ${isActive ? "active" : ""}`
      }
      style={{ paddingLeft: "32px", fontSize: "12.5px" }}
    >
      <span
        className="inline-block w-1 h-1 rounded-full mr-1 opacity-60"
        style={{ background: "var(--color-fg-muted)" }}
      />
      {children}
    </NavLink>
  );
}

export default Sidebar;
